import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  Paper,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { PaintBlockReportModalStyles } from '../../Insights/InsightsExplorer/PaintBlock/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { theme } from '@/theme/theme';
import { abyssColors } from '@/theme/colors';
import { useRef, useState } from 'react';
import { SmallButton } from '@/components/UserManagement/styles';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import {
  File as GQLFile,
  FileNameTypes,
  FileUploadStatus,
  usePreSignedUrlToUploadFilesMutation,
  useUpdateFileStatusForPaintBlockReportManualUploadsMutation,
  useAddAndRemoveFilesMutation,
  FileCreateInput,
  ReportDetailsDocument,
} from '@/__generated__/graphql';
import { useRouter } from '@/hooks';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { generateUUIDv4 } from '@/utils/string';
import { uploadFileToUrl } from '../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/Tabs/UploadFileToUrl';
import { Attachment } from '../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/types';
import * as state from '@/components/Analysis/state';
import { useAtomValue } from 'jotai';
import { UnattachedImages } from './PaintBlockReportImageList/UnattachedImages';
import { AttachedImages } from './PaintBlockReportImageList/AttachedImages';
import { extractErrorMessageFromUnknownError } from '@/utils/error';

const ACCEPTED_FILES_FORMATS = ['png', 'jpg', 'jpeg'];
const INPUT_ACCEPT = ACCEPTED_FILES_FORMATS.map((value) => `.${value}`).join(', ');

type Props = {
  open: boolean;
  onClose: () => void;
};

type UploadBoxProps = {
  sx?: SxProps;
  onClick?: () => void;
};

const RefetchReportDetailsParams = {
  refetchQueries: [ReportDetailsDocument],
};

const UploadBox = ({ sx, onClick }: UploadBoxProps) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        boxSizing: 'border-box',
        border: '1px dashed black',
        width: '20rem',
        borderRadius: '5px',
        borderColor: theme.palette.text.secondary,
        justifyItems: 'center',
        alignContent: 'center',
        py: 2,
        cursor: 'pointer',
        ...sx,
      }}
    >
      <ArrowUpwardIcon fontSize={'small'} sx={{ color: theme.palette.text.secondary }} />
      <Typography fontSize={'12px'} fontWeight={400} color={theme.palette.text.secondary}>
        Upload
      </Typography>
    </Box>
  );
};

export const ReportImageUploadModal = ({ onClose, open }: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploadImages, setUploadImages] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const { showSnackBar } = useSnackBarMessage({});
  const router = useRouter();

  const [imagesToAttach, setImagesToAttach] = useState<GQLFile[]>([]);
  const [imagesToDetach, setImagesToDetach] = useState<string[]>([]);

  const report = useAtomValue(state.selectedReport);

  const reportId = router?.query?.reportid?.toString();
  const structureId = useGetStructureId();

  const [getPresignedUrls] = usePreSignedUrlToUploadFilesMutation();
  const [updateImageStatuses] = useUpdateFileStatusForPaintBlockReportManualUploadsMutation();
  const [addOrRemovePoiImages] = useAddAndRemoveFilesMutation(RefetchReportDetailsParams);

  if (!report) return <></>;

  const reportPois = report?.pointOfInterests;
  const reportFiles = report?.files;

  const openInputDialog = () => {
    const element = inputRef?.current;
    if (element) {
      element.click();
    }
  };

  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    setUploadImages(files ? [...files] : []);
  };

  const resetAllState = () => {
    setUploadImages([]);
    setImagesToAttach([]);
    setImagesToDetach([]);
    setUploading(false);
  };

  const onConfirm = async () => {
    const uuids = uploadImages.map(() => generateUUIDv4());

    try {
      setUploading(true);

      // Creating istances for files on Fabric server
      // This will give us back a URL for every image we send here
      // We then use given URLs to actually upload the file, to the CDN
      const preSignedResponse = await getPresignedUrls({
        variables: {
          input: {
            structureId,
            referenceId: reportId?.toString() || '',
            collectionName: 'report',
            fileNameType: FileNameTypes.UserAttachments,
            files: uploadImages.map((image, index) => ({
              name: image.name,
              mimeType: image.type,
              size: image.size,
              uuid: uuids[index],
            })),
          },
        },
      });

      // Files contains the database entries for the files
      const files = preSignedResponse?.data?.preSignedUrlToUploadFiles;

      // Checking if the number of uploaded images match the database entries
      if (!files || files?.length !== uploadImages.length) {
        throw new Error('Could not fetch files from pre signed URL');
      }

      // Uploading the images to the signed URL that is returned from our database
      const promises = files.map((item, index) =>
        uploadFileToUrl(
          {
            file: uploadImages[index],
            type: uploadImages[index].type,
          } as Attachment,
          item.url
        )
      );

      // Doing the actual upload now, this will throw
      // and error even if one upload fails
      await Promise.all(promises);

      // Since we're here, it's safe to say that
      // Every step above has succeeded and we have
      // 1- Created DB entries for our images
      // 2- Uploaded those images to the CDN
      // Now whats left is to update the files' statuses to 'SUCCESSFULL'
      await updateImageStatuses({
        variables: {
          input: {
            collectionName: 'report',
            referenceId: reportId || '',
            fileUpdates: files.map((file) => ({
              fileId: file.id,
              uploadStatus: FileUploadStatus.Success,
            })),
          },
        },
      });

      // Syncing POIs images with report
      const addFiles: Array<FileCreateInput> = imagesToAttach.map((file) => ({
        ...file,
        uploadStatus: file.uploadStatus as FileUploadStatus,
        __typename: undefined,
        id: undefined,
      }));

      await addOrRemovePoiImages({
        variables: {
          input: {
            collectionName: 'report',
            referenceId: reportId || '',
            addFiles,
            removeFileIds: imagesToDetach,
          },
        },
      });

      showSnackBar('Files uploaded', 'success');
      onClose();
    } catch (error: unknown) {
      const message = extractErrorMessageFromUnknownError(error);
      showSnackBar(message, 'error');
    } finally {
      resetAllState();
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        resetAllState();
        onClose();
      }}
      sx={PaintBlockReportModalStyles.modal}
    >
      <Paper
        sx={{
          ...PaintBlockReportModalStyles.paper,
          width: '45%',
          maxHeight: '85vh',
          py: 2,
        }}
      >
        <input
          type="file"
          ref={inputRef}
          accept={INPUT_ACCEPT}
          hidden
          multiple
          onChange={onFileInputChange}
        />
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          mb={2}
          px={3}
        >
          <Typography fontSize={'20px'} fontWeight={700}>
            Adding more images
          </Typography>
          <IconButton
            onClick={() => {
              resetAllState();
              onClose();
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        <hr color={abyssColors.primary[200]} style={{ height: '1px', border: 'none' }} />

        <Box
          px={3}
          sx={{
            overflow: 'hidden',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
            height: '62vh',
            paddingTop: '14px',
          }}
        >
          <Typography fontWeight={500} fontSize={'12px'}>
            Upload from your device
          </Typography>

          <Grid spacing={2} container width={'100%'} alignItems={'center'} my={2}>
            <Grid item xl={3} lg={3} md={3} sm={6} xs={6}>
              <UploadBox onClick={openInputDialog} sx={{ height: '12rem' }} />
            </Grid>

            {uploadImages.map((imageData, index) => (
              <Grid item xl={3} lg={3} md={3} sm={6} xs={6} key={index}>
                <Box
                  sx={{
                    width: '100%',
                    height: '15rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={URL.createObjectURL(imageData)}
                    alt={`Uploaded preview ${index + 1}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>

          <hr color={abyssColors.primary[200]} style={{ height: '1px', border: 'none' }} />

          <Typography fontSize={'12px'} fontWeight={500} my={2}>
            Select from chase items
          </Typography>

          <Grid container direction={'row'} rowGap={2}>
            <UnattachedImages
              pois={reportPois}
              reportFiles={reportFiles?.filter(
                (file) => file.uploadStatus === FileUploadStatus.Success
              )}
              setImagesToAttach={setImagesToAttach}
              imagesToAttach={imagesToAttach}
            />

            <AttachedImages
              pois={reportPois}
              reportFiles={reportFiles?.filter(
                (file) => file.uploadStatus === FileUploadStatus.Success
              )}
              imagesToDetach={imagesToDetach}
              setImagesToDetach={setImagesToDetach}
            />
          </Grid>
        </Box>

        <hr
          color={abyssColors.primary[200]}
          style={{ height: '1px', border: 'none', marginBottom: '2%' }}
        />

        <Stack direction={'row'} gap={1} justifyContent={'flex-end'} px={3}>
          <SmallButton variant="outlined" size="small" onClick={onClose}>
            Cancel
          </SmallButton>
          <SmallButton onClick={onConfirm} disabled={uploading} contained>
            {uploading && <CircularProgress size={20} color="secondary" sx={{ mx: 1 }} />}
            Add
          </SmallButton>
        </Stack>
      </Paper>
    </Modal>
  );
};
